import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { userReportType } from "../../../../../services/api/types/reports/reports";

interface UserReportTableProps {
  users: userReportType[];
  portalName?: string;
}

export const UserReportTable: React.FC<UserReportTableProps> = ({
  users,
  portalName,
}) => {
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startScrollLeft: number; trackW: number; thumbW: number } | null>(null);
  const [scrollbar, setScrollbar] = useState({ show: false, thumbWidth: 0, thumbLeft: 0 });
  const SCROLL_ARROW_W = 44;

  const refreshScrollbar = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el || dragRef.current) return;
    const scrollable = el.scrollWidth > el.clientWidth + 1;
    if (!scrollable) { setScrollbar(s => ({ ...s, show: false })); return; }
    const trackW = el.clientWidth - SCROLL_ARROW_W * 2;
    const thumbW = Math.max((el.clientWidth / el.scrollWidth) * trackW, 44);
    const maxScroll = el.scrollWidth - el.clientWidth;
    const thumbLeft = maxScroll > 0 ? (el.scrollLeft / maxScroll) * (trackW - thumbW) : 0;
    setScrollbar({ show: true, thumbWidth: thumbW, thumbLeft });
  }, [SCROLL_ARROW_W]);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", refreshScrollbar, { passive: true });
    const ro = new ResizeObserver(refreshScrollbar);
    ro.observe(el);
    refreshScrollbar();
    return () => { el.removeEventListener("scroll", refreshScrollbar); ro.disconnect(); };
  }, [refreshScrollbar]);

  useEffect(() => {
    const move = (clientX: number) => {
      const d = dragRef.current;
      if (!d) return;
      const el = tableScrollRef.current;
      if (!el) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      const scale = maxScroll / Math.max(d.trackW - d.thumbW, 1);
      const newScrollLeft = Math.max(0, Math.min(maxScroll, d.startScrollLeft + (clientX - d.startX) * scale));
      el.scrollLeft = newScrollLeft;
      if (thumbRef.current) {
        const newLeft = maxScroll > 0 ? (newScrollLeft / maxScroll) * (d.trackW - d.thumbW) : 0;
        thumbRef.current.style.left = `${newLeft}px`;
      }
    };
    const end = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      refreshScrollbar();
    };
    const onMM = (e: MouseEvent) => move(e.clientX);
    const onTM = (e: TouchEvent) => {
      if (!dragRef.current) return;
      e.preventDefault();
      move(e.touches[0].clientX);
    };
    document.addEventListener("mousemove", onMM);
    document.addEventListener("mouseup", end);
    document.addEventListener("touchmove", onTM, { passive: false });
    document.addEventListener("touchend", end);
    return () => {
      document.removeEventListener("mousemove", onMM);
      document.removeEventListener("mouseup", end);
      document.removeEventListener("touchmove", onTM);
      document.removeEventListener("touchend", end);
    };
  }, [refreshScrollbar]);

  const onThumbDragStart = (clientX: number) => {
    const el = tableScrollRef.current;
    if (!el) return;
    dragRef.current = { startX: clientX, startScrollLeft: el.scrollLeft, trackW: el.clientWidth - SCROLL_ARROW_W * 2, thumbW: scrollbar.thumbWidth };
  };

  const scrollByAmount = (amount: number) => {
    tableScrollRef.current?.scrollBy({ left: amount, behavior: "smooth" });
  };

  if (!users || users.length === 0) {
    return (
      <Paper elevation={1} sx={{ border: "1px solid #EAECF0" }}>
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ color: "#667085", fontSize: "14px" }}>
            No users found.
          </Typography>
        </Box>
      </Paper>
    );
  }

  const additionalKeys = users
    .flatMap((u) => Object.keys(u.userAttributes ?? {}))
    .filter((key, i, arr) => arr.indexOf(key) === i)
    .slice(0, 4);

  const renderValue = (value: any) => {
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  return (
    <Paper elevation={1} sx={{ border: "1px solid #EAECF0", p: 0 }}>
      {/* Portal name header — outside TableContainer so sticky top works relative to page scroll */}
      {portalName && (
        <Box
          sx={{
            backgroundColor: "#EAECF0",
            color: "#475467",
            fontWeight: 600,
            fontSize: "14px",
            p: "12px",
            borderBottom: "1px solid #EAECF0",
            borderRadius: "8px 8px 0 0",
          }}
        >
          Portal: {portalName}
        </Box>
      )}
      <TableContainer
        ref={tableScrollRef}
        sx={{
          mb: "1px",
          borderRadius: portalName ? "0 0 8px 8px" : "8px",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-x",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Table sx={{ minWidth: { xs: (1 + additionalKeys.length) * 200, md: "auto" } }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#F9FAFB",
                "& th": {
                  color: "#475467",
                  fontWeight: 500,
                  fontSize: "12px",
                  borderBottom: "1px solid #EAECF0",
                  p: "12px",
                },
              }}
            >
              <TableCell sx={{ minWidth: { xs: 220, md: "auto" }, position: { xs: "sticky", md: "static" }, left: { xs: 0, md: "auto" }, zIndex: { xs: 2, md: "auto" }, backgroundColor: "#F9FAFB" }}>User information</TableCell>
              {additionalKeys.map((key) => (
                <TableCell key={key} sx={{ minWidth: { xs: 160, md: "auto" } }}>{key.replace(/_/g, " ")}</TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {users.map((user, index) => {
              const attrs = user.userAttributes || {};
              return (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#fff" : "#F9FAFB",
                    "& td": {
                      borderBottom: "1px solid #EAECF0",
                      color: "#475467",
                      fontSize: "12px",
                      py: 1.5,
                      px: 2,
                    },
                    "&:last-child td": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <TableCell sx={{ position: { xs: "sticky", md: "static" }, left: { xs: 0, md: "auto" }, zIndex: { xs: 1, md: "auto" }, backgroundColor: { xs: index % 2 === 0 ? "#fff" : "#F9FAFB", md: "inherit" } }}>
                    <Typography color="#101828" fontSize="12px">
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography color="#475467" fontSize="12px">
                      {user.email}
                    </Typography>
                  </TableCell>

                  {additionalKeys.map((key) => (
                    <TableCell key={key}>{renderValue(attrs[key])}</TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {scrollbar.show && (
        <Box sx={{ display: "flex", alignItems: "center", height: SCROLL_ARROW_W, backgroundColor: "#fff", userSelect: "none" }}>
          <Box
            sx={{ width: SCROLL_ARROW_W, height: SCROLL_ARROW_W, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
            onMouseDown={() => scrollByAmount(-100)}
            onTouchStart={(e) => { e.preventDefault(); scrollByAmount(-100); }}
          >
            <svg width="10" height="16" viewBox="0 0 6 10"><polygon points="5,1 1,5 5,9" fill="#344054"/></svg>
          </Box>
          <Box sx={{ flex: 1, position: "relative", height: SCROLL_ARROW_W }}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                height: 16,
                width: scrollbar.thumbWidth,
                left: scrollbar.thumbLeft,
                backgroundColor: "#eaecf0",
                borderRadius: "10px",
                cursor: "grab",
                touchAction: "none",
                "&:active": { cursor: "grabbing" },
              }}
              ref={thumbRef}
              onMouseDown={(e) => { e.preventDefault(); onThumbDragStart(e.clientX); }}
              onTouchStart={(e) => { e.preventDefault(); onThumbDragStart(e.touches[0].clientX); }}
            />
          </Box>
          <Box
            sx={{ width: SCROLL_ARROW_W, height: SCROLL_ARROW_W, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
            onMouseDown={() => scrollByAmount(100)}
            onTouchStart={(e) => { e.preventDefault(); scrollByAmount(100); }}
          >
            <svg width="10" height="16" viewBox="0 0 6 10"><polygon points="1,1 5,5 1,9" fill="#344054"/></svg>
          </Box>
        </Box>
      )}
    </Paper>
  );
};
